import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SearchBox from "@/components/feature/SearchBox";
import WorkCard from "@/components/base/WorkCard";
import EmptyState from "@/components/base/EmptyState";
import { allWorks, searchWorks } from "@/utils/film";
import { news } from "@/mocks/news";

const TABS = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
  { value: "news", label: "资讯" },
] as const;

type TabValue = (typeof TABS)[number]["value"];

export default function Search() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("q")?.trim() ?? "";
  const [tab, setTab] = useState<TabValue>("all");

  useEffect(() => {
    setTab("all");
  }, [keyword]);

  useEffect(() => {
    document.title = keyword
      ? `“${keyword}” 搜索结果 - 白虎影视平台`
      : "搜索影视 - 白虎影视平台";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        keyword
          ? `白虎影视平台中与“${keyword}”相关的电影、电视剧与影视资讯搜索结果，支持按类型切换查看。`
          : "在白虎影视平台搜索电影、电视剧与影视资讯。"
      );
    }
  }, [keyword]);

  const workResults = useMemo(() => (keyword ? searchWorks(keyword) : []), [keyword]);

  const newsResults = useMemo(() => {
    if (!keyword) return [];
    const q = keyword.toLowerCase();
    return news.filter((item) =>
      `${item.title} ${item.summary} ${item.content.join(" ")} ${item.category}`
        .toLowerCase()
        .includes(q)
    );
  }, [keyword]);

  const popular = useMemo(
    () => [...allWorks].sort((a, b) => b.hot - a.hot).slice(0, 6),
    []
  );

  const movieResults = workResults.filter((item) => item.category === "movie");
  const tvResults = workResults.filter((item) => item.category === "tv");

  const tabsWithCount = TABS.map((item) => ({
    ...item,
    count:
      item.value === "all"
        ? workResults.length + newsResults.length
        : item.value === "movie"
        ? movieResults.length
        : item.value === "tv"
        ? tvResults.length
        : newsResults.length,
  }));

  const visibleWorks =
    tab === "movie" ? movieResults : tab === "tv" ? tvResults : tab === "all" ? workResults : [];
  const showNews = tab === "all" || tab === "news";
  const totalVisible = visibleWorks.length + (showNews ? newsResults.length : 0);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-accent-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">搜索</span>
      </nav>

      <h1 className="mt-5 text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
        {keyword ? (
          <>
            “<span className="text-accent-600">{keyword}</span>” 的搜索结果
          </>
        ) : (
          "搜索影视"
        )}
      </h1>

      <div className="mt-5 max-w-2xl">
        <SearchBox initialValue={keyword} size="lg" autoFocus={!keyword} />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {tabsWithCount.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => setTab(item.value)}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              tab === item.value
                ? "bg-accent-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {item.label}
            <span className="ml-1 text-xs opacity-80">{item.count}</span>
          </button>
        ))}
      </div>

      {keyword ? (
        <p className="mt-6 text-sm text-foreground-500">
          共找到 <strong className="text-accent-600">{totalVisible}</strong> 条与 “{keyword}”
          相关的内容（搜索为前端演示功能）
        </p>
      ) : null}

      <div className="mt-6">
        {!keyword ? (
          <div className="space-y-10">
            <EmptyState
              icon="ri-search-line"
              title="输入关键词开始搜索"
              description="试试搜索电影或电视剧的名称、类型，例如“科幻”“悬疑”或具体作品名称。"
            />
            <PopularSection works={popular} />
          </div>
        ) : totalVisible === 0 ? (
          <div className="space-y-10">
            <EmptyState
              icon="ri-emotion-sad-line"
              title="没有找到相关内容"
              description={`未找到与“${keyword}”匹配的影视作品或资讯，换个关键词试试，或看看下面的热门推荐。`}
            />
            <PopularSection works={popular} />
          </div>
        ) : (
          <div className="space-y-10">
            {visibleWorks.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                {visibleWorks.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>
            ) : null}

            {showNews && newsResults.length > 0 ? (
              <div>
                <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-200 text-secondary-900">
                    <i className="ri-newspaper-line text-sm"></i>
                  </span>
                  相关资讯
                </h2>
                <ul className="mt-4 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  {newsResults.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/news/detail/${item.id}`}
                        className="flex flex-col gap-1 px-5 py-4 transition-colors hover:bg-background-100 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <span className="text-sm font-medium text-foreground-900 sm:text-base">
                          {item.title}
                        </span>
                        <span className="flex shrink-0 items-center gap-2 text-xs text-foreground-500">
                          <span className="rounded-md bg-accent-100 px-2 py-0.5 text-accent-800">
                            {item.category}
                          </span>
                          {item.publishDate}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function PopularSection({ works }: { works: ReturnType<typeof searchWorks> }) {
  if (!works.length) return null;
  return (
    <div>
      <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-500 text-sm text-background-50">
          <i className="ri-fire-line"></i>
        </span>
        热门推荐
      </h2>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {works.map((work) => (
          <WorkCard key={work.id} work={work} />
        ))}
      </div>
    </div>
  );
}